import { action, when, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import UiStore from '../stores/UiStore';
import { IIcon } from '../models/icon';
import { ILine } from '../models/line';
import { IPlace } from '../models/place';
import { IRoute } from '../models/route';

import { getLocale } from '../../utils/pathHelper';

interface ITimeline {
  ui?: UiStore;
}

const polyLineOptions = {
  strokeColor: '#007bff',
  strokeWeight: 4,
};

@inject('ui')
@observer
class Timeline extends React.Component<ITimeline, any> {
  constructor(props: ITimeline) {
    super(props);
    makeObservable(this);
  }

  public render() {
    const { ui } = this.props;
    const timeline = [ this.renderOriginSelectBox() ];
  
    if (ui.isAccessAvailable) {
      const { access, currentPlace } = ui;
      const { path, duration } = access;
  
      path.forEach((line: ILine, i) => {
        const { origin, dest, route } = line;
        if (!origin || !dest) { return; }
        // Origin (Skip first item for the select box)
        if (i !== 0) {
          timeline.push(this.renderPlace(origin, origin.place_id === currentPlace.place_id));
        }
        // Route
        timeline.push(this.renderRoute(route));
        // Destination
        if (i === path.length - 1) {
          timeline.push(this.renderPlace(dest, dest.place_id === currentPlace.place_id));
        }
      });
    } else {
      timeline.push(this.renderErrors());
    }

    return (
      <div>
        {timeline}
      </div>
    );
  }

  @action.bound
  public handleClick(e: any) {
    e.preventDefault();
    const { ui } = this.props;
    const href = e.target.getAttribute('href') || '';
    const place_id = href.replace(/#place-/, '');
    const target = ui.places.forEach((place: IPlace, i: number) => {
      if (place.place_id === place_id) {
        ui.placeOpenIndex = i;
      } 
    });
  }

  public renderRoute(route: IRoute) {
    const { t } = this.props.ui;
    const { duration } = route;
    const name = route[`name_${getLocale({ underscore: true })}`] || '';
    const hasPrice = route.price != null && Number(route.price) > 0;
    return (
      <div className="access-ui__route-item" key={`${name}-${route.duration}-${route.price}`}>
        <div className="inner">
          <p>
            {this.renderIcon({ name: route.icon } as IIcon)}
            {name}
          </p>
          <p>
            {duration &&
              <span style={{ marginRight: 4 }}>{t.minute({ minute: duration })}</span>
            }
            {hasPrice && `JPY ${Number(route.price).toLocaleString()}`}
          </p>
        </div>
      </div>
    )
  }

  public renderIcon(icon: IIcon) {
    if (!icon.name) { return null; }
    // Depends on Rails Fontawesome gem
    let name = '';
    switch(icon.name) {
      case 'walk':
        name = 'fa-walking'; break; 
      case 'train':
        name = 'fa-train'; break;
      case 'car':
        name = 'fa-car'; break;
      case 'bus':
        name = 'fa-bus'; break;
      case 'taxi':
        name = 'fa-taxi'; break;
      case 'other':
        name = 'fa-arrows-alt-v'; break;
    }
    return <i className={`fas fa-fw ${name} access-ui__route-item-icon`} />;
  }

  public renderPlace(place: IPlace, isActive: boolean) {
    const name = place[`name_${getLocale({ underscore: true })}`] || '';
    const key = `#place-${place.place_id}`;
    return (
      <a
        className={`access-ui__timeline-item ${isActive && 'active'}`}
        key={key}
        href={key}
        onClick={this.handleClick}
      >
        <i className="fas fa-circle" />
        {name}
      </a>
    )
  }

  public renderOriginSelectBox() {
    const { ui } = this.props;
    const klass = 'access-ui__select';
    return (
      <div className={klass} key={klass}>
        <select value={ui.originIndex} onChange={ui.updateOrign}>
          {ui.origins.map((origin, i) => (
            <option key={`origin-${origin.place_id}`} value={i}>{origin[`name_${getLocale({ underscore: true })}`]}</option>
          ))}
        </select>
        <div className="access-ui__select-arrow"></div>
      </div>
    )
  }

  public renderErrors() {
    const { errors } = this.props.ui;
    if (errors.length === 0) { return null; }
    const klass = 'access-ui-errors';
    return (
      <div className={klass} key={klass}>
        <ul>
          {errors.map((error) => <li key={error}>{error}</li>)}
        </ul>
      </div>
    );
  }
}

export default Timeline
